/**
 * ProgressionLevelWidget - Уровень по дню жизни игрока
 * =====================================================
 *
 * 4 кривые на одной временной оси (день жизни игрока):
 *   Campaign / Elite / TD / Absorption.
 * Данные из ClickHouse (gamegears.prod_events_hole_alien). Диапазон дат
 * дашборда трактуется как КОГОРТА (по дате регистрации acc_reg_ts).
 * Переключатель avg / median меняет отображаемую агрегацию по игрокам.
 */

import React, { useState } from 'react';
import { BaseWidget, useBaseWidget } from './BaseWidget';
import { useWidgetData } from '../../hooks/useWidgetData';
import { MetricsService } from '../../services/metricsService';
import { withDefaultDateRange } from '../../utils/defaultDateRange';
import { completedDaysSubtitle } from '../../utils/completedDaysWindow';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { Tooltip } from '../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../ui/responsive-chart';
import { progression as progressionSpecs } from '@ggstat/chart-specs';

// Описание кривых: ключ режима в ответе (<key>Avg / <key>Median), подпись, цвет —
// общие с MCP (shared/chart-specs)
const CURVES = progressionSpecs.levelByDay.curves;

export function ProgressionLevelWidget() {
  // Агрегация по игрокам: среднее или медиана (общий тумблер для всех кривых)
  const [aggregation, setAggregation] = useState('avg');
  const suffix = aggregation === 'median' ? 'Median' : 'Avg';

  // Когорта обязательна: без диапазона дат сервер отвечает 400 (иначе запрос
  // читает все партиции таблицы событий). Пустой диапазон → последние 30 дней.
  const widgetData = useWidgetData(
    async (filters, options) =>
      MetricsService.getProgressionLevelByDay(withDefaultDateRange(filters), options)
  );

  const widgetProps = useBaseWidget(widgetData);
  const { data } = widgetProps;

  const points = data?.data || [];
  const dateRange = data?.dateRange;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
      <div className="bg-background border rounded-lg shadow-lg p-3">
        <p className="font-medium mb-2">День жизни: {label}</p>
        {payload.map((entry) => (
          <p key={entry.name} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {Number(entry.value).toLocaleString('ru-RU', { maximumFractionDigits: 2 })}
          </p>
        ))}
      </div>
    );
  };

  const Controls = () => (
    <ToggleGroup
      type="single"
      value={aggregation}
      onValueChange={(value) => value && setAggregation(value)}
    >
      <ToggleGroupItem value="avg" aria-label="Среднее">Среднее</ToggleGroupItem>
      <ToggleGroupItem value="median" aria-label="Медиана">Медиана</ToggleGroupItem>
    </ToggleGroup>
  );

  return (
    <BaseWidget
      {...widgetProps}
      title="Уровень по дню жизни (когорта)"
      subtitle={completedDaysSubtitle('Когорта (регистрация)', dateRange)}
      headerAction={<Controls />}
      emptyMessage="Нет данных за выбранную когорту"
      skeletonRows={8}
    >
      <ResponsiveContainer width="100%" height={420}>
        <LineChart data={points} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis
            dataKey="dayOfGame"
            type="number"
            domain={['dataMin', 'dataMax']}
            allowDecimals={false}
            tick={{ fill: 'currentColor' }}
            className="text-xs"
            label={{ value: 'День жизни', position: 'insideBottom', offset: -2 }}
          />
          <YAxis
            tick={{ fill: 'currentColor' }}
            className="text-xs"
            allowDecimals={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          {CURVES.map((curve) => (
            <Line
              key={curve.key}
              type="monotone"
              dataKey={`${curve.key}${suffix}`}
              name={curve.label}
              stroke={curve.color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </BaseWidget>
  );
}

export default ProgressionLevelWidget;
